import _ from 'lodash'
import Big from 'big.js'

export default class ProjectCalculations {
  /**
   * Constructor.
   *
   * @param {Object} project
   */
  constructor (project) {
    this.update(project)
  }

  /**
   * Update the project object.
   *
   * @param {Object} project
   */
  update (project) {
    this.project = project
  }

  /**
   * Run calculations against the project.
   *
   * @returns {this}
   */
  calculate () {
    this.project.financials.fundingRequired = this.getFundingRequired()
    this.project.financials.fundingSecured = this.getFundingSecured()
    this.project.financials.ltc = this.getLtc()
    this.project.financials.ltcIncFees = this.getLtcIncFees()
    this.project.financials.ltgdv = this.getLtgdv()
    this.project.financials.ltgdvIncFees = this.getLtgdvIncFees()

    return this
  }

  /**
   * Get the total build costs.
   *
   * @returns {Number}
   */
  getTotalBuildCosts () {
    const {
      construction,
      contingency,
      groundWorks,
      landscaping
    } = this.project.financials.build

    const result = Big(construction || 0)
      .plus(contingency || 0)
      .plus(groundWorks || 0)
      .plus(landscaping || 0)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Calculate the build cost per sqm.
   *
   * @returns {Number}
   */
  getBuildCostPerSqm () {
    const { totalFloorArea } = this.project
    const buildCosts = this.getTotalBuildCosts()

    if (Big(buildCosts).gt(0.00) && Big(totalFloorArea || 0).gt(0.00)) {
      const result = Big(buildCosts)
        .div(totalFloorArea)
        .toFixed(2)

      return _.toNumber(result)
    }

    return 0
  }

  /**
   * Get the total funding amount for a specific charge.
   *
   * @param {String} type
   * @param {String} exclude
   * @returns {Number}
   */
  getFundingChargeAmount (type = 'First', exclude = '') {
    const amounts = []

    this.getProject().funding.forEach(({ _id, amount, charge }) => {
      if (charge === type && !exclude.includes(_id)) {
        amounts.push(_.toNumber(amount))
      }
    })

    return _.sum(amounts)
  }

  /**
   * Get the LTV for a project funding.
   *
   * @param {Object} funding
   * @returns {Number}
   */
  getFundingLtv (loan) {
    const { _id, amount, charge } = loan
    const gdv = this.getGdv()
    const firstCharges = this.getFundingChargeAmount('First', _id)
    const secondCharges = this.getFundingChargeAmount('Second', _id)
    let ltv = 0

    if (charge === 'First') {
      ltv = Big(amount).div(gdv).times(100).toFixed(2)
    }

    if (charge === 'Second') {
      ltv = Big(amount).plus(firstCharges).div(gdv).times(100).toFixed(2)
    }

    if (charge === 'Third') {
      ltv = Big(amount).plus(firstCharges).plus(secondCharges).div(gdv).times(100).toFixed(2)
    }

    return _.toNumber(ltv)
  }

  /**
   * Get the required amount of funding.
   *
   * @returns {Number}
   */
  getFundingRequired () {
    const secured = this.getFundingSecured()
    const costs = this.getTotalCost()
    const result = Big(costs).minus(secured).toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the amount of funding secured.
   *
   * @returns {Number}
   */
  getFundingSecured () {
    let amounts = []

    _.map(this.project.funding, 'amount').forEach(amount => {
      amounts.push(_.toNumber(amount))
    })

    return _.sum(amounts)
  }

  /**
   * Get the GDV.
   *
   * @returns {Number}
   */
  getGdv () {
    return _.get(this.project.financials, 'gdv') || 0.00
  }

  /**
   * Get the lending secured.
   *
   * @returns {Number}
   */
  getLendingSecured () {
    return _.get(this.project.financials, 'lendingSecured') || 0.00
  }

  /**
   * Get the loan costs.
   *
   * @returns {Number}
   */
  getLoanCosts () {
    return _.get(this.project.financials, 'loanCost') || 0.00
  }

  /**
   * Get the LTC.
   *
   * @returns {Number}
   */
  getLtc () {
    const costs = this.getTotalCost()
    const required = this.getFundingRequired()
    const secured = this.getLendingSecured()

    const total = Big(secured)
      .plus(required)
      .toFixed(2)
    const result = Big(total)
      .div(costs)
      .times(100)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the LTC including fees.
   *
   * @returns {Number}
   */
  getLtcIncFees () {
    const costs = this.getTotalCost()
    const loan = this.getLoanCosts()
    const required = this.getFundingRequired()

    const total = Big(loan)
      .plus(required)
      .toFixed(2)
    const result = Big(total)
      .div(costs)
      .times(100)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the LTGDV.
   *
   * @returns {Number}
   */
  getLtgdv () {
    const gdv = this.getGdv()
    const required = this.getFundingRequired()
    const secured = this.getLendingSecured()

    const total = Big(secured)
      .plus(required)
      .toFixed(2)
    const result = Big(total)
      .div(gdv)
      .times(100)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the LTGDV including fees.
   *
   * @returns {Number}
   */
  getLtgdvIncFees () {
    const costs = this.getLoanCosts()
    const gdv = this.getGdv()
    const required = this.getFundingRequired()

    const total = Big(costs)
      .plus(required)
      .toFixed(2)
    const result = Big(total)
      .div(gdv)
      .times(100)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the total percentage that we have funded.
   *
   * @returns {Number}
   */
  getPercentageFunded () {
    const fundingSecured = this.getFundingSecured()
    const totalCost = this.getTotalCost()

    if (!Big(fundingSecured).gt(0.00) || !Big(totalCost).gt(0.00)) return '0'

    const result = Big(fundingSecured)
      .div(totalCost)
      .times(100)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the total other costs.
   *
   * @returns {Number}
   */
  getTotalOtherCosts () {
    const { section106Cil } = this.project.financials.other

    const result = Big(section106Cil || 0)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Calculate the project cost per sqm.
   *
   * @returns {Number}
   */
  getProjectCostPerSqm () {
    const { totalFloorArea } = this.project
    const totalCosts = this.getTotalCost()

    if (Big(totalCosts).gt(0.00) && Big(totalFloorArea).gt(0.00)) {
      const result = Big(totalCosts)
        .div(totalFloorArea)
        .toFixed(2)

      return _.toNumber(result)
    }

    return 0
  }

  /**
   * Get the total professional costs.
   *
   * @returns {Number}
   */
  getTotalFinanceCosts () {
    const {
      interestProvision,
      legalFees,
      lendingFees,
      other,
      valuationFees
    } = this.project.financials.finance

    const result = Big(interestProvision || 0)
      .plus(legalFees || 0)
      .plus(lendingFees || 0)
      .plus(other || 0)
      .plus(valuationFees || 0)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the total professional costs.
   *
   * @returns {Number}
   */
  getTotalProfessionalCosts () {
    const {
      architectFees,
      monitoringSurveyors,
      projectManagement,
      warrantyCosts
    } = this.project.financials.professional

    const result = Big(architectFees || 0)
      .plus(monitoringSurveyors || 0)
      .plus(projectManagement || 0)
      .plus(warrantyCosts || 0)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the profit margin cost.
   *
   * @returns {Number}
   */
  getProfitMarginCost () {
    const profit = this.getTotalProfit()
    const costs = this.getTotalCost()

    if (Big(profit).gt(0.00) && Big(costs).gt(0.00)) {
      const result = Big(profit)
        .div(costs)
        .times(100)
        .toFixed(2)

      return _.toNumber(result)
    }

    return 0
  }

  /**
   * Get the profit margin GDV.
   *
   * @returns {Number}
   */
  getProfitMarginGdv () {
    const { gdv } = this.project.financials
    const profit = this.getTotalProfit()

    if (Big(gdv).gt(0.00) && Big(profit).gt(0.00)) {
      const result = Big(profit)
        .div(gdv)
        .times(100)
        .toFixed(2)

      return _.toNumber(result)
    }

    return 0
  }

  /**
   * Get the project.
   *
   * @returns {Object}
   */
  getProject () {
    return this.project
  }

  /**
   * Get the total sales costs.
   *
   * @returns {Number}
   */
  getTotalSalesCosts () {
    const {
      agencyFee,
      dressings,
      marketingMaterial,
      saleLegalCosts
    } = this.project.financials.sales

    const result = Big(agencyFee || 0)
      .plus(dressings || 0)
      .plus(marketingMaterial || 0)
      .plus(saleLegalCosts || 0)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the total site costs.
   *
   * @returns {Number}
   */
  getTotalSiteCosts () {
    const {
      introducerCommission,
      legalFees,
      purchasePrice,
      stampDuty
    } = this.project.financials.site

    const result = Big(introducerCommission || 0)
      .plus(legalFees || 0)
      .plus(purchasePrice || 0)
      .plus(stampDuty || 0)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the total costs.
   *
   * @returns {Number}
   */
  getTotalCost () {
    const build = this.getTotalBuildCosts()
    const finance = this.getTotalFinanceCosts()
    const other = this.getTotalOtherCosts()
    const professional = this.getTotalProfessionalCosts()
    const sales = this.getTotalSalesCosts()
    const site = this.getTotalSiteCosts()

    const result = Big(build)
      .plus(finance)
      .plus(other)
      .plus(professional)
      .plus(sales)
      .plus(site)
      .toFixed(2)

    return _.toNumber(result)
  }

  /**
   * Get the total profit.
   *
   * @returns {Number}
   */
  getTotalProfit () {
    const { gdv } = this.project.financials
    const totalCosts = this.getTotalCost()

    const result = Big(gdv)
      .minus(totalCosts)
      .toFixed(2)

    return _.toNumber(result)
  }
}
